import { useCallback, useEffect, useRef } from "react";
import { useMap } from "react-leaflet";
import * as L from "leaflet";
import "leaflet-draw";
import * as turf from "@turf/turf";
import {
  useRulerModalContext,
  MAP_TOOLS,
  MAP_UNITS,
} from "../../../context/RulerContext";
import { useLayers } from "../../../context/LayersContext";
import {
  mockLayers,
  defaultDimensionValue,
  wmsDimensionParam,
} from "../../../global/constants/layers";
import { geoServerBaseUrl } from "../../../services/geoServer";
import { geoServerAuthHeaders } from "../../../services/geoServerAuth";
import { pickActiveRasterTypename } from "../../../utils/activeRasterTypename";
import { LIcon } from "../../LIcon";
import {
  installEdgeMeasureLabels,
  uninstallEdgeMeasureLabels,
  updateEdgeMeasureLabels,
} from "../../../utils/edgeMeasureLabels";
import {
  DRAWINGS_PANE_NAME,
  DRAWING_HIT_STROKE_CLASS_NAME,
} from "../../../utils/mapRegistry";
import { formatAreaByUnit, computeAreaKm2, AREA_THRESHOLD_M2 } from "../../../utils/areaFormatting";
import {
  doSegmentsIntersect,
  distanceBetween,
} from "../../../utils/segmentIntersection";
import type { Feature } from "geojson";

const PROFILE_LINE_COLOR = "#E07B39";

L.drawLocal.draw.handlers.polyline.error =
  "<strong>Erro:</strong> o traçado não pode se cruzar!";

type LayerEventCleanup = {
  layer: L.Layer;
  event: "click" | "mouseover" | "mouseout";
  handler: L.LeafletEventHandlerFn;
};

const LAYER_COLOR = "#0C2856";
const FREEHAND_BLOCKED_COLOR = "#DC2626";
const DRAW_VERTEX_ICON = L.divIcon({
  className:
    "bg-white border border-[#0C2856] rounded-full shadow-[0_1px_3px_rgba(0,0,0,0.4)]",
  iconSize: [8, 8],
  iconAnchor: [4, 4],
});
const LIVE_LABEL_ICON_CLASS = "bg-transparent border-0 pointer-events-none";
const LIVE_LABEL_SPAN_CLASS =
  "inline-block translate-x-3 -translate-y-1/2 whitespace-nowrap rounded border border-[rgba(12,40,86,0.32)] bg-[rgba(12,40,86,0.94)] px-1.5 py-[3px] text-xs font-bold leading-[1.2] text-white shadow-[0_1px_4px_rgba(0,0,0,0.22)]";

// Medido em pixels de tela, não em metros reais — assim o mesmo gesto de
// mouse sempre conta como "clique" ou sempre como "traço", em vez de
// alternar conforme o nível de zoom (metros por pixel muda com o zoom).
const FREEHAND_CLICK_MAX_LENGTH_PX = 15;
const FREEHAND_MIN_POINT_SPACING_PX = 3;

const FREEHAND_ERROR_LABEL_SPAN_CLASS =
  "inline-block translate-x-3 -translate-y-1/2 whitespace-nowrap rounded border border-[rgba(153,27,27,0.35)] bg-[rgba(153,27,27,0.94)] px-1.5 py-[3px] text-xs font-bold leading-[1.2] text-white shadow-[0_1px_4px_rgba(0,0,0,0.22)]";

const createFreehandErrorIcon = (message: string) =>
  L.divIcon({
    className: LIVE_LABEL_ICON_CLASS,
    iconSize: [0, 0],
    iconAnchor: [0, 0],
    html: `<span class="${FREEHAND_ERROR_LABEL_SPAN_CLASS}">${message}</span>`,
  });

const createLiveMeasureIcon = (label: string) =>
  L.divIcon({
    className: LIVE_LABEL_ICON_CLASS,
    iconSize: [0, 0],
    iconAnchor: [0, 0],
    html: `<span class="${LIVE_LABEL_SPAN_CLASS}">${label}</span>`,
  });

const PE_MAINLAND_BOUNDS = {
  minLat: -9.5,
  maxLat: -7.0,
  minLng: -41.5,
  maxLng: -34.8,
};

const PE_NORONHA_BOUNDS = {
  minLat: -4.0,
  maxLat: -3.7,
  minLng: -32.6,
  maxLng: -32.2,
};

const isInsidePernambuco = (lat: number, lng: number) => {
  const inMainland =
    lat >= PE_MAINLAND_BOUNDS.minLat &&
    lat <= PE_MAINLAND_BOUNDS.maxLat &&
    lng >= PE_MAINLAND_BOUNDS.minLng &&
    lng <= PE_MAINLAND_BOUNDS.maxLng;

  const inNoronha =
    lat >= PE_NORONHA_BOUNDS.minLat &&
    lat <= PE_NORONHA_BOUNDS.maxLat &&
    lng >= PE_NORONHA_BOUNDS.minLng &&
    lng <= PE_NORONHA_BOUNDS.maxLng;

  return inMainland || inNoronha;
};

interface ToolsHandlerProps {
  featureGroupRef: React.RefObject<L.FeatureGroup>;
  onInvalidLocation?: (message: string) => void;
  onMapDrawingClick?: (id: string) => void;
  onMapDrawingHover?: (id: string | null) => void;
}

const HOVER_STYLE = { color: "#FFD700", weight: 5, opacity: 1 };
const DEFAULT_STYLE = { color: "#0C2856", weight: 3, opacity: 0.8 };
const MARKER_HOVER_FILTER = "drop-shadow(0 0 5px #FFD700) brightness(1.15)";

const setMarkerFilter = (layer: L.Layer, filter: string) => {
  if (!(layer instanceof L.Marker)) return;
  const el = layer.getElement();
  if (el) el.style.filter = filter;
};

export default function ToolsHandler({
  featureGroupRef,
  onInvalidLocation,
  onMapDrawingClick,
  onMapDrawingHover,
}: ToolsHandlerProps) {
  const map = useMap();
  const {
    activeFunction,
    activeFunctionHandler,
    setMeasure,
    addLayersToMap,
    drawings,
    clearAllDrawings,
    setProfileData,
    setIsLoadingProfile,
    setProfileLayerName,
    profileLayerRef,
    profileGenRef,
  } = useRulerModalContext();

  const { layers, layerTimeValues, layerElevationValues } = useLayers();
  const layersRef = useRef(layers);
  const layerTimeValuesRef = useRef(layerTimeValues);
  const layerElevationValuesRef = useRef(layerElevationValues);
  useEffect(() => { layersRef.current = layers; }, [layers]);
  useEffect(() => { layerTimeValuesRef.current = layerTimeValues; }, [layerTimeValues]);
  useEffect(() => { layerElevationValuesRef.current = layerElevationValues; }, [layerElevationValues]);

  const handlerRef = useRef<any>(null);
  const activeToolStartTime = useRef<number>(0);
  const internalActiveTool = useRef<string | null>(null);
  const isDrawingFreehand = useRef(false);
  const freehandMouseDownRef = useRef<
    ((e: L.LeafletMouseEvent) => void) | null
  >(null);
  const freehandMouseMoveRef = useRef<
    ((e: L.LeafletMouseEvent) => void) | null
  >(null);
  const freehandMouseUpRef = useRef<(() => void) | null>(null);
  const freehandPoints = useRef<L.LatLng[]>([]);
  const freehandPolyline = useRef<L.Polyline | null>(null);
  const liveDistancePoints = useRef<L.LatLng[]>([]);
  const liveDistanceLabel = useRef<L.Marker | null>(null);
  const freehandErrorTooltip = useRef<L.Marker | null>(null);

  const clearLiveDistanceLabel = useCallback(() => {
    liveDistanceLabel.current?.remove();
    liveDistanceLabel.current = null;
    liveDistancePoints.current = [];
  }, []);

  const clearFreehandErrorTooltip = useCallback(() => {
    freehandErrorTooltip.current?.remove();
    freehandErrorTooltip.current = null;
  }, []);

  const showFreehandErrorTooltip = useCallback(
    (cursorLatLng: L.LatLng, message: string) => {
      const icon = createFreehandErrorIcon(message);

      if (freehandErrorTooltip.current) {
        freehandErrorTooltip.current.setLatLng(cursorLatLng);
        freehandErrorTooltip.current.setIcon(icon);
        return;
      }

      freehandErrorTooltip.current = L.marker(cursorLatLng, {
        interactive: false,
        keyboard: false,
        icon,
      }).addTo(map);
    },
    [map]
  );

  const formatLiveDistance = useCallback((distanceInKm: number) => {
    if (distanceInKm < 1) return `${Math.round(distanceInKm * 1000)} m`;
    return `${distanceInKm.toFixed(2)} ${MAP_UNITS.KM}`;
  }, []);

  const getDrawingVertexLatLngs = useCallback(
    (layers?: L.LayerGroup): L.LatLng[] => {
      if (!layers) return [];

      const points: L.LatLng[] = [];
      layers.eachLayer((layer: L.Layer) => {
        if (layer instanceof L.Marker) points.push(layer.getLatLng());
      });
      return points;
    },
    []
  );

  const updateLiveDistanceLabel = useCallback(
    (cursorLatLng: L.LatLng) => {
      if (liveDistancePoints.current.length === 0) return;

      const points = [...liveDistancePoints.current, cursorLatLng];
      if (points.length < 2) return;

      const line = turf.lineString(
        points.map((point) => [point.lng, point.lat])
      );
      const distanceInKm = turf.length(line, { units: "kilometers" });
      const label = formatLiveDistance(distanceInKm);

      const icon = createLiveMeasureIcon(label);

      if (liveDistanceLabel.current) {
        liveDistanceLabel.current.setLatLng(cursorLatLng);
        liveDistanceLabel.current.setIcon(icon);
        return;
      }

      liveDistanceLabel.current = L.marker(cursorLatLng, {
        interactive: false,
        keyboard: false,
        icon,
      }).addTo(map);
    },
    [formatLiveDistance, map]
  );

  const updateLiveAreaLabel = useCallback(
    (cursorLatLng: L.LatLng) => {
      updateLiveDistanceLabel(cursorLatLng);
    },
    [updateLiveDistanceLabel]
  );

  useEffect(() => {
    if (!featureGroupRef.current || !map) return;
    addLayersToMap(featureGroupRef.current, map);
  }, [drawings, featureGroupRef, addLayersToMap, map]);

  useEffect(() => {
    drawings.forEach((drawing) => {
      if (!(drawing.layer instanceof L.Polyline)) return;

      const shouldShowLabels =
        drawing.isVisible &&
        (drawing.type === MAP_TOOLS.MEASURE ||
          drawing.type === MAP_TOOLS.MEASURE_AREA);

      if (shouldShowLabels) {
        installEdgeMeasureLabels(
          drawing.layer,
          map,
          drawing.type === MAP_TOOLS.MEASURE_AREA
        );
      } else {
        uninstallEdgeMeasureLabels(drawing.layer);
      }
    });
  }, [drawings, map]);

  useEffect(() => {
    const updateVisibleDrawingLabels = () => {
      drawings.forEach((drawing) => {
        if (!drawing.isVisible || !(drawing.layer instanceof L.Polyline))
          return;
        if (
          drawing.type !== MAP_TOOLS.MEASURE &&
          drawing.type !== MAP_TOOLS.MEASURE_AREA
        )
          return;

        updateEdgeMeasureLabels(
          drawing.layer,
          map,
          drawing.type === MAP_TOOLS.MEASURE_AREA
        );
      });
    };

    map.on("draw:editvertex", updateVisibleDrawingLabels);
    map.on("draw:editmove", updateVisibleDrawingLabels);
    map.on("draw:edited", updateVisibleDrawingLabels);

    return () => {
      map.off("draw:editvertex", updateVisibleDrawingLabels);
      map.off("draw:editmove", updateVisibleDrawingLabels);
      map.off("draw:edited", updateVisibleDrawingLabels);
    };
  }, [drawings, map]);

  useEffect(() => {
    const cleanups: LayerEventCleanup[] = [];

    drawings.forEach((drawing) => {
      const { layer, id } = drawing;

      if (onMapDrawingClick) {
        const clickHandler = (e: L.LeafletMouseEvent) => {
          L.DomEvent.stopPropagation(e);
          onMapDrawingClick(id);
        };
        layer.on("click", clickHandler as L.LeafletEventHandlerFn);
        cleanups.push({ layer, event: "click", handler: clickHandler as L.LeafletEventHandlerFn });
      }

      if (onMapDrawingHover) {
        let savedStyle: L.PathOptions = DEFAULT_STYLE;
        const overHandler = () => {
          if (layer instanceof L.Path) {
            const { color, weight, opacity } = layer.options;
            savedStyle = { color, weight, opacity };
            layer.setStyle(HOVER_STYLE);
          }
          setMarkerFilter(layer, MARKER_HOVER_FILTER);
          onMapDrawingHover(id);
        };
        const outHandler = () => {
          if (layer instanceof L.Path) layer.setStyle(savedStyle);
          setMarkerFilter(layer, "");
          onMapDrawingHover(null);
        };
        layer.on("mouseover", overHandler);
        layer.on("mouseout", outHandler);
        cleanups.push({ layer, event: "mouseover", handler: overHandler });
        cleanups.push({ layer, event: "mouseout", handler: outHandler });
      }
    });

    return () => {
      cleanups.forEach(({ layer, event, handler }) => layer.off(event, handler));
    };
  }, [drawings, onMapDrawingClick, onMapDrawingHover]);

  useEffect(() => {
    const onCreated = (e: any) => {
      const layer = e.layer;
      const type = e.layerType;
      let isValidLocation = false;

      if (type === "marker") {
        const latlng = layer.getLatLng();
        isValidLocation = isInsidePernambuco(latlng.lat, latlng.lng);
      } else if (type === "polyline" || type === "polygon") {
        const latLngs = layer.getLatLngs();
        let allPoints: any[] = [];

        if (Array.isArray(latLngs)) {
          const flat = latLngs.flat(Infinity);
          allPoints = flat;
        }

        if (allPoints.length > 0) {
          isValidLocation = allPoints.every((point: any) => {
            if (point && point.lat && point.lng) {
              return isInsidePernambuco(point.lat, point.lng);
            }
            return false;
          });
        }
      }

      if (!isValidLocation) {
        if (onInvalidLocation) {
          onInvalidLocation(
            "Ação não permitida! Por favor, realize desenhos ou marcações apenas na região de Pernambuco e arredores."
          );
        }
        map.removeLayer(layer);
        setTimeout(() => activeFunctionHandler(null), 0);
        return;
      }

      // Handle profile tool separately — does not go through the normal confirm flow
      if (type === "polyline" && internalActiveTool.current === MAP_TOOLS.PROFILE) {
        if (profileLayerRef.current) {
          uninstallEdgeMeasureLabels(profileLayerRef.current as L.Polyline);
          try { (profileLayerRef.current as L.Layer).remove(); } catch {}
          featureGroupRef.current?.removeLayer(profileLayerRef.current as L.Layer);
          profileLayerRef.current = null;
        }

        (layer as L.Polyline).setStyle({
          color: PROFILE_LINE_COLOR,
          weight: 3,
          dashArray: "8 5",
          opacity: 0.9,
          pane: DRAWINGS_PANE_NAME,
        });
        featureGroupRef.current?.addLayer(layer);
        profileLayerRef.current = layer;

        const latLngs = (layer as L.Polyline).getLatLngs() as L.LatLng[];
        if (latLngs.length < 2) {
          activeFunctionHandler(null);
          return;
        }

        installEdgeMeasureLabels(layer as L.Polyline, map, false);

        const lineCoords = latLngs.map((ll: L.LatLng) => [ll.lng, ll.lat]);
        const line = turf.lineString(lineCoords);
        const totalLengthKm = turf.length(line, { units: "kilometers" });

        const activeRaster = layersRef.current
          .map((title: string) => mockLayers.find((l) => l.title === title && l.type === "raster"))
          .find(Boolean);

        if (!activeRaster?.workspace) {
          setProfileData([]);
          setProfileLayerName(null);
          activeFunctionHandler(null);
          return;
        }

        const generation = ++profileGenRef.current;
        setIsLoadingProfile(true);
        setProfileLayerName(activeRaster.title);
        setProfileData(null);
        activeFunctionHandler(null);

        const NUM_SAMPLES = 60;
        const eps = 0.001;
        // A mesma camada exibida no mapa no zoom atual (ver RenderGeoServerLayers):
        // acima do limiar, o raster detalhado de 30 m substitui o de 200 m, e o
        // perfil tem que amostrar valores dessa mesma camada, não da base.
        const activeTypename = pickActiveRasterTypename(
          map.getZoom(),
          activeRaster.layerName,
          activeRaster.detailedLayerName,
          activeRaster.detailedLayerZoomThreshold
        );

        (async () => {
          const points = Array.from({ length: NUM_SAMPLES + 1 }, (_, i) => {
            const dist = (i / NUM_SAMPLES) * totalLengthKm;
            const pt = turf.along(line, dist, { units: "kilometers" });
            return { dist, coords: pt.geometry.coordinates };
          });

          const results = await Promise.all(
            points.map(async ({ dist, coords }) => {
              const [lng, lat] = coords;
              const bbox = `${lng - eps},${lat - eps},${lng + eps},${lat + eps}`;
              const params = new URLSearchParams({
                SERVICE: "WMS",
                VERSION: "1.1.1",
                REQUEST: "GetFeatureInfo",
                LAYERS: `${activeRaster.workspace}:${activeTypename}`,
                QUERY_LAYERS: `${activeRaster.workspace}:${activeTypename}`,
                FORMAT: "image/png",
                STYLES: "",
                INFO_FORMAT: "application/json",
                FEATURE_COUNT: "1",
                X: "1",
                Y: "1",
                SRS: "EPSG:4326",
                WIDTH: "3",
                HEIGHT: "3",
                BBOX: bbox,
              });
              if (activeRaster.temporal?.enabled) {
                if (activeRaster.temporal.dimension === "time") {
                  const t = layerTimeValuesRef.current[activeRaster.layerName];
                  if (t) params.set("TIME", t);
                } else {
                  // elevation e dimensões customizadas (direction, year): o
                  // valor consultado tem que ser o da fatia que está na tela.
                  const value =
                    layerElevationValuesRef.current[activeRaster.layerName] ??
                    defaultDimensionValue(activeRaster);
                  if (value !== undefined) {
                    params.set(
                      wmsDimensionParam(
                        activeRaster.temporal.dimension
                      ).toUpperCase(),
                      String(value)
                    );
                  }
                }
              }
              try {
                const resp = await fetch(
                  `${geoServerBaseUrl}/${activeRaster.workspace}/wms?${params}`,
                  { headers: geoServerAuthHeaders(activeRaster.workspace) }
                );
                const data = await resp.json();
                const props = data?.features?.[0]?.properties;
                if (!props) return { distance: dist, value: null };
                const numVal = Object.values(props).find(
                  (v) => typeof v === "number" && isFinite(v as number)
                );
                return { distance: dist, value: numVal !== undefined ? (numVal as number) : null };
              } catch {
                return { distance: dist, value: null };
              }
            })
          );

          if (generation !== profileGenRef.current) return; // resultado obsoleto
          setProfileData(results);
          setIsLoadingProfile(false);
        })();

        return;
      }

      // Singleton: remove any previous drawing before showing the new one
      clearAllDrawings();

      featureGroupRef.current?.addLayer(layer);

      if (type === "polyline" || type === "polygon") {
        installEdgeMeasureLabels(layer, map, type === "polygon");
      }

      try {
        if (type === "marker") {
          const latlng = layer.getLatLng();
          const lat = latlng.lat;
          const lng = latlng.lng;

          setMeasure("Buscando endereço...", layer, 0, "");

          const url = `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=18&addressdetails=1`;

          fetch(url, { headers: { "User-Agent": "Atlas-Front/1.0" } })
            .then((res) => {
              if (!res.ok) throw new Error("Erro na rede");
              return res.json();
            })
            .then((data) => {
              if (data && data.address) {
                const addr = data.address;
                const road =
                  addr.road ||
                  addr.pedestrian ||
                  addr.path ||
                  addr.footway ||
                  "";
                const neighborhood =
                  addr.suburb || addr.neighbourhood || addr.residential || "";
                const city =
                  addr.city ||
                  addr.town ||
                  addr.municipality ||
                  addr.village ||
                  "";
                const state = addr.state || "";

                if (!road && !neighborhood) {
                  setMeasure(
                    `${lat.toFixed(5)}, ${lng.toFixed(5)}`,
                    layer,
                    0,
                    ""
                  );
                  return;
                }

                const parts = [road, neighborhood, city, state].filter(
                  (p) => p && p.trim() !== ""
                );
                const finalAddress =
                  parts.length > 0
                    ? parts.join(", ")
                    : `${lat.toFixed(5)}, ${lng.toFixed(5)}`;

                setMeasure(finalAddress, layer, 0, "");
              } else {
                setMeasure(
                  `${lat.toFixed(5)}, ${lng.toFixed(5)}`,
                  layer,
                  0,
                  ""
                );
              }
            })
            .catch((err) => {
              console.error("Erro ao buscar endereço:", err);
              setMeasure(`${lat.toFixed(5)}, ${lng.toFixed(5)}`, layer, 0, "");
            });
        }

        if (type === "polyline") {
          const latLngs = layer.getLatLngs();
          let cleanCoords: any[] = [];
          if (Array.isArray(latLngs)) {
            if ((latLngs[0] as any).lat) {
              cleanCoords = latLngs;
            } else if (Array.isArray(latLngs[0])) {
              cleanCoords = latLngs.flat();
            }
          }

          const lineCoords = cleanCoords.map((pt: any) => [pt.lng, pt.lat]);
          if (lineCoords.length < 2) return;

          const line = turf.lineString(lineCoords);
          const distanceInKm = turf.length(line, { units: "kilometers" });
          const distanceInMeters = distanceInKm * 1000;

          const result = `${distanceInKm.toFixed(2)}${MAP_UNITS.KM}`;
          setMeasure(result, layer, distanceInMeters, MAP_UNITS.KM);
        }

        if (type === "polygon") {
          const geoJsonFeature = (layer as L.Polygon).toGeoJSON() as Feature;
          const areaInSquareMeters = geoJsonFeature.geometry
            ? computeAreaKm2(geoJsonFeature.geometry) * 1_000_000
            : 0;

          let result = "";
          let unit: string = MAP_UNITS.M2;

          if (areaInSquareMeters > AREA_THRESHOLD_M2.SQ_KM) {
            result = formatAreaByUnit(areaInSquareMeters / AREA_THRESHOLD_M2.SQ_KM, MAP_UNITS.KM2);
            unit = MAP_UNITS.KM2;
          } else if (areaInSquareMeters > AREA_THRESHOLD_M2.HECTARE) {
            result = formatAreaByUnit(areaInSquareMeters / AREA_THRESHOLD_M2.HECTARE, MAP_UNITS.HECTARE);
            unit = MAP_UNITS.HECTARE;
          } else {
            result = formatAreaByUnit(areaInSquareMeters, MAP_UNITS.M2);
          }

          setMeasure(result, layer, areaInSquareMeters, unit);
        }
      } catch (error) {
        console.error("Error calculating measurement:", error);
      }
    };

    map.on("draw:created", onCreated);
    return () => {
      map.off("draw:created", onCreated);
    };
  }, [
    map,
    featureGroupRef,
    setMeasure,
    onInvalidLocation,
    activeFunctionHandler,
    clearAllDrawings,
  ]);

  useEffect(() => {
    const setCursor = (value: string) => {
      const container = map?.getContainer?.();
      if (container?.style) {
        container.style.cursor = value;
      }
    };

    const onFreehandMouseDown = (e: L.LeafletMouseEvent) => {
      isDrawingFreehand.current = true;
      freehandPoints.current = [e.latlng];

      freehandPolyline.current = L.polyline([e.latlng], {
        color: LAYER_COLOR,
        weight: 4,
        opacity: 1,
        pane: DRAWINGS_PANE_NAME,
      }).addTo(map);

      map.dragging.disable();
    };

    const onFreehandMouseMove = (e: L.LeafletMouseEvent) => {
      if (!isDrawingFreehand.current || !freehandPolyline.current) return;

      const points = freehandPoints.current;
      const candidatePoint = map.latLngToContainerPoint(e.latlng);

      // Ignora tremor sub-pixel do mouse: sem isso, o mesmo tremor mínimo vira
      // um desvio de muitos metros reais com o zoom bem afastado, e só aparece
      // como um ziguezague quando o traçado é visto depois em outro zoom.
      const lastRecordedPixel = map.latLngToContainerPoint(
        points[points.length - 1]
      );
      if (
        distanceBetween(candidatePoint, lastRecordedPixel) <
        FREEHAND_MIN_POINT_SPACING_PX
      ) {
        return;
      }

      if (points.length >= 2) {
        const newSegmentStart = map.latLngToContainerPoint(
          points[points.length - 1]
        );

        let crossesExisting = false;
        for (let i = 0; i < points.length - 2; i += 1) {
          const priorStart = map.latLngToContainerPoint(points[i]);
          const priorEnd = map.latLngToContainerPoint(points[i + 1]);
          if (
            doSegmentsIntersect(
              newSegmentStart,
              candidatePoint,
              priorStart,
              priorEnd
            )
          ) {
            crossesExisting = true;
            break;
          }
        }

        if (crossesExisting) {
          freehandPolyline.current.setStyle({ color: FREEHAND_BLOCKED_COLOR });
          showFreehandErrorTooltip(
            e.latlng,
            L.drawLocal.draw.handlers.polyline.error
          );
          return;
        }
      }

      freehandPolyline.current.setStyle({ color: LAYER_COLOR });
      clearFreehandErrorTooltip();
      freehandPoints.current.push(e.latlng);
      freehandPolyline.current.setLatLngs(freehandPoints.current);
    };

    const onFreehandMouseUp = () => {
      if (!isDrawingFreehand.current || !freehandPolyline.current) return;

      isDrawingFreehand.current = false;
      map.dragging.enable();
      setCursor("");

      const points = freehandPoints.current;

      const isSingleClick = points.length < 2;
      const totalPixelLength = points.slice(1).reduce((total, point, index) => {
        const previousPixel = map.latLngToContainerPoint(points[index]);
        const currentPixel = map.latLngToContainerPoint(point);
        return total + distanceBetween(previousPixel, currentPixel);
      }, 0);
      const isVeryShort =
        !isSingleClick && totalPixelLength < FREEHAND_CLICK_MAX_LENGTH_PX;

      if (isSingleClick || isVeryShort) {
        if (freehandPolyline.current) freehandPolyline.current.remove();
        freehandPolyline.current = null;
        freehandPoints.current = [];

        const point = points[0];
        if (!isInsidePernambuco(point.lat, point.lng)) {
          if (onInvalidLocation) {
            onInvalidLocation(
              "Ação não permitida! Por favor, realize desenhos ou marcações apenas na região de Pernambuco e arredores."
            );
          }
          setTimeout(() => activeFunctionHandler(null), 0);
          return;
        }

        clearAllDrawings();
        const marker = L.circleMarker([point.lat, point.lng], {
          radius: 3,
          color: LAYER_COLOR,
          fillColor: LAYER_COLOR,
          fillOpacity: 1,
          weight: 0,
          pane: DRAWINGS_PANE_NAME,
        });
        featureGroupRef.current?.addLayer(marker);
        setMeasure(null, marker);
        return;
      }

      const hasPointOutside = points.some(
        (pt) => !isInsidePernambuco(pt.lat, pt.lng)
      );
      if (hasPointOutside) {
        if (freehandPolyline.current) freehandPolyline.current.remove();
        freehandPolyline.current = null;
        freehandPoints.current = [];
        if (onInvalidLocation) {
          onInvalidLocation(
            "Ação não permitida! Por favor, realize desenhos ou marcações apenas na região de Pernambuco e arredores."
          );
        }
        setTimeout(() => activeFunctionHandler(null), 0);
        return;
      }

      const finishedPolyline = freehandPolyline.current;
      finishedPolyline.setStyle({ color: LAYER_COLOR });
      clearAllDrawings();
      clearFreehandErrorTooltip();
      featureGroupRef.current?.addLayer(finishedPolyline);
      setMeasure(null, finishedPolyline);
      freehandPolyline.current = null;
      freehandPoints.current = [];
    };

    const onMeasureDrawVertex = (e: any) => {
      liveDistancePoints.current = getDrawingVertexLatLngs(e.layers);
    };

    const onMeasureMouseMove = (e: L.LeafletMouseEvent) => {
      updateLiveDistanceLabel(e.latlng);
    };

    const onMeasureAreaMouseMove = (e: L.LeafletMouseEvent) => {
      updateLiveAreaLabel(e.latlng);
    };

    const drawMap = map as any;
    const previousDrawControlTooltips = drawMap.options?.drawControlTooltips;

    if (activeFunction) {
      activeToolStartTime.current = Date.now();
      internalActiveTool.current = activeFunction;
      setCursor("crosshair");

      if (drawMap.options) drawMap.options.drawControlTooltips = false;
      const options = {
        color: LAYER_COLOR,
        weight: 3,
        opacity: 0.8,
        pane: DRAWINGS_PANE_NAME,
        className: DRAWING_HIT_STROKE_CLASS_NAME,
      };
      let newHandler: any = null;

      if (activeFunction === MAP_TOOLS.LOCATION)
        newHandler = new L.Draw.Marker(drawMap, { icon: LIcon });

      if (activeFunction === MAP_TOOLS.MEASURE)
        newHandler = new L.Draw.Polyline(drawMap, {
          shapeOptions: options,
          icon: DRAW_VERTEX_ICON,
          metric: true,
          showLength: false,
          allowIntersection: false,
        });

      if (activeFunction === MAP_TOOLS.MEASURE) {
        map.on("draw:drawvertex", onMeasureDrawVertex);
        map.on("mousemove", onMeasureMouseMove);
        map.on("draw:drawstop", clearLiveDistanceLabel);
        map.on("draw:created", clearLiveDistanceLabel);
      }

      if (activeFunction === MAP_TOOLS.MEASURE_AREA)
        newHandler = new L.Draw.Polygon(drawMap, {
          allowIntersection: false,
          icon: DRAW_VERTEX_ICON,
          showArea: false,
          shapeOptions: options,
        });

      if (activeFunction === MAP_TOOLS.MEASURE_AREA) {
        map.on("draw:drawvertex", onMeasureDrawVertex);
        map.on("mousemove", onMeasureAreaMouseMove);
        map.on("draw:drawstop", clearLiveDistanceLabel);
        map.on("draw:created", clearLiveDistanceLabel);
      }

      if (activeFunction === MAP_TOOLS.DRAW) {
        freehandMouseDownRef.current = onFreehandMouseDown;
        freehandMouseMoveRef.current = onFreehandMouseMove;
        freehandMouseUpRef.current = onFreehandMouseUp;
        map.on("mousedown", onFreehandMouseDown);
        map.on("mousemove", onFreehandMouseMove);
        map.on("mouseup", onFreehandMouseUp);
      }

      if (activeFunction === MAP_TOOLS.PROFILE)
        newHandler = new L.Draw.Polyline(drawMap, {
          shapeOptions: {
            color: PROFILE_LINE_COLOR,
            weight: 3,
            opacity: 0.9,
            dashArray: "8 5",
            pane: DRAWINGS_PANE_NAME,
          },
          icon: DRAW_VERTEX_ICON,
          metric: true,
          showLength: false,
        });

      if (activeFunction === MAP_TOOLS.PROFILE) {
        map.on("draw:drawvertex", onMeasureDrawVertex);
        map.on("mousemove", onMeasureMouseMove);
        map.on("draw:drawstop", clearLiveDistanceLabel);
        map.on("draw:created", clearLiveDistanceLabel);
      }

      if (newHandler && typeof newHandler.enable === "function") {
        newHandler.enable();
        handlerRef.current = newHandler;
      }
    } else {
      setCursor("");
      internalActiveTool.current = null;
    }

    return () => {
      if (handlerRef.current) {
        handlerRef.current.disable();
        handlerRef.current = null;
      }

      if (drawMap.options) {
        drawMap.options.drawControlTooltips = previousDrawControlTooltips;
      }

      if (freehandMouseDownRef.current) {
        map.off("mousedown", freehandMouseDownRef.current);
        freehandMouseDownRef.current = null;
      }
      if (freehandMouseMoveRef.current) {
        map.off("mousemove", freehandMouseMoveRef.current);
        freehandMouseMoveRef.current = null;
      }
      if (freehandMouseUpRef.current) {
        map.off("mouseup", freehandMouseUpRef.current);
        freehandMouseUpRef.current = null;
      }

      if (freehandPolyline.current) {
        freehandPolyline.current.remove();
        freehandPolyline.current = null;
      }
      map.off("draw:drawvertex", onMeasureDrawVertex);
      map.off("mousemove", onMeasureMouseMove);
      map.off("mousemove", onMeasureAreaMouseMove);
      map.off("draw:drawstop", clearLiveDistanceLabel);
      map.off("draw:created", clearLiveDistanceLabel);
      clearLiveDistanceLabel();
      clearFreehandErrorTooltip();
      isDrawingFreehand.current = false;
      freehandPoints.current = [];
    };
  }, [
    activeFunction,
    map,
    activeFunctionHandler,
    featureGroupRef,
    setMeasure,
    onInvalidLocation,
    clearAllDrawings,
    clearLiveDistanceLabel,
    getDrawingVertexLatLngs,
    updateLiveDistanceLabel,
    updateLiveAreaLabel,
    clearFreehandErrorTooltip,
    showFreehandErrorTooltip,
  ]);

  return null;
}
